import { useEffect, useRef, useState, useCallback } from 'react';
import {
  AudioButtonContainer,
  AudioControls,
  AudioProgressBar,
  AudioProgressBarSeeker,
  StyledIconButton,
  StyledText,
  TimeContainer,
  AudioProgressBarFiller,
} from './AudioRecording.styles';

interface Props {
  audioSource: string;
  audioType: string;
  'data-testid'?: string;
}
export const AudioRecording = ({ audioSource, audioType, ...props }: Props) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [animated, setAnimated] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) {
      return;
    }

    const onLoadedMetadata = () => {
      setDuration(audioElement.duration);
    };

    const onTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
    };

    const onStart = () => {
      setIsPlaying(true);
    };
    const onStop = () => {
      setIsPlaying(false);
    };

    audioElement.addEventListener('loadedmetadata', onLoadedMetadata);
    audioElement.addEventListener('timeupdate', onTimeUpdate);
    audioElement.addEventListener('play', onStart);
    audioElement.addEventListener('ended', onStop);
    audioElement.addEventListener('pause', onStop);

    return () => {
      audioElement.removeEventListener('loadedmetadata', onLoadedMetadata);
      audioElement.removeEventListener('timeupdate', onTimeUpdate);
      audioElement.removeEventListener('play', onStart);
      audioElement.removeEventListener('ended', onStop);
      audioElement.removeEventListener('pause', onStop);
    };
  }, []);

  const playPauseToggle = useCallback(async () => {
    if (!audioRef.current?.paused) {
      audioRef.current?.pause();
    } else {
      await audioRef.current?.play();
    }
  }, []);

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  const progressBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const progressBar = progressBarRef.current;
    const getProgressBarPercentage = (mouseX: number) => {
      if (progressBar) {
        const progressBarBounds = progressBar.getBoundingClientRect();

        if (mouseX < progressBarBounds.left) {
          return 0;
        }

        if (mouseX > progressBarBounds.right) {
          return 100;
        }

        return (mouseX - progressBarBounds.left) / progressBarBounds.width;
      }

      return undefined;
    };

    const updateCurrentTime = (e: MouseEvent) => {
      if (e.clientX === 0) {
        return;
      }

      const percentage = getProgressBarPercentage(e.clientX);
      const audioElement = audioRef.current;
      if (audioElement && percentage !== undefined) {
        audioElement.currentTime = audioElement.duration * percentage;
      }
    };

    const onDrag = (e: MouseEvent) => {
      updateCurrentTime(e);
    };

    const onClick = (e: MouseEvent) => {
      updateCurrentTime(e);
    };

    const span = document.createElement('span');
    span.style.opacity = '0';
    span.textContent = 'text';
    document.body.appendChild(span);

    const onDragStart = (e: DragEvent) => {
      e.dataTransfer?.setDragImage(span, 0, 0);
      setAnimated(false);
      audioRef.current?.pause();
    };

    const onDragEnd = () => {
      setAnimated(true);
      audioRef.current?.play();
    };

    if (progressBar) {
      progressBar.addEventListener('click', onClick);
      progressBar.addEventListener('dragstart', onDragStart);
      progressBar.addEventListener('drag', onDrag);
      progressBar.addEventListener('dragend', onDragEnd);

      return () => {
        progressBar.removeEventListener('click', onClick);
        progressBar.removeEventListener('dragstart', onDragStart);
        progressBar.removeEventListener('drag', onDrag);
        progressBar.removeEventListener('dragend', onDragEnd);

        span.remove();
      };
    }
  }, []);

  const [src, setSrc] = useState<string>();
  useEffect(() => {
    fetch(audioSource)
      .then((response) => response.blob())
      .then((blob) => {
        var reader = new FileReader();
        reader.onload = function () {
          if (!this.result) {
            return;
          }

          const base64 = this.result as string;
          setSrc(base64);
        };
        reader.readAsDataURL(blob);
      });
  }, [audioSource]);

  return (
    <AudioButtonContainer data-testid={props['data-testid']}>
      <audio ref={audioRef} src={src} />
      <AudioControls>
        <StyledIconButton
          variant="primary"
          size="large"
          iconName={isPlaying ? 'uil uil-pause' : 'uil uil-play'}
          aria-label="play-pause"
          color="white"
          onClick={playPauseToggle}
        />

        <TimeContainer>
          <StyledText>
            {formatTime(currentTime <= 0.5 ? 0 : Math.max(1, currentTime))}{' '}
          </StyledText>

          <AudioProgressBar ref={progressBarRef} draggable="true">
            <AudioProgressBarFiller
              percentage={(currentTime / duration) * 100}
              animated={animated}
              data-testid="progress-bar-filler"
            />
            <AudioProgressBarSeeker
              percentage={(currentTime / duration) * 100}
              animated={animated}
            />
          </AudioProgressBar>

          <StyledText lighterColor={true}>
            {formatTime(Math.max(duration, 1))}
          </StyledText>
        </TimeContainer>
      </AudioControls>
    </AudioButtonContainer>
  );
};
